import * as React from 'react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Col, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { TableGlobalFilter } from '../TableGlobalFilter/TableGlobalFilter';
import { TablePagination } from '../TablePagination/TablePagination';
import { Column, TableInstance, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { useDefaultColumn } from '../../hooks/ReactTableHooks';
import { authAxios } from '../../services/AxiosService';
import { useParams } from 'react-router';
import { formatDateTime } from '../../formatters/DateTimeFormatter';

export interface IRecordView {
  recordViewId: string;
  userName: string;
  auditUserId: string;
  auditTimestamp: number;
}

interface IProps {
  selectRow?: (userId: string) => void;
}

export const FileRecordViews: React.FC<IProps> = () => {
  const [recordViews, setRecordViews] = useState<IRecordView[]>([]);
  // const [users, setUsers] = useState<IUser[]>([]);
  const params = useParams<{ fileId: string }>();

  // useEffect(() => {
  //   authAxios.get('/api/users/admin/staff').then((response) => {
  //     setUsers(response.data);
  //   });
  // }, []);

  useEffect(() => {
    authAxios.get('/api/files/admin/' + params.fileId + '/record-views').then((response) => {
      setRecordViews(response.data);
    });
  }, [params.fileId]);

  const columns: Column<IRecordView>[] = useMemo(
    () => [
      {
        Header: 'User Name',
        id: 'user-name',
        accessor: (recordView: IRecordView) => {
          return recordView.userName;
        },
      },
      {
        Header: 'Date',
        accessor: 'auditTimestamp',
        Cell: (cell: any) => {
          if (cell && cell.row.original && cell.row.original.auditTimestamp) {
            return <span>{formatDateTime(new Date(cell.row.original.auditTimestamp))}</span>;
          } else {
            return <span />;
          }
        },
      },
    ],
    []
  );

  const data: IRecordView[] = useMemo(() => recordViews, [recordViews]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,

    prepareRow,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,

    state,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  }: TableInstance<IRecordView> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageSize: 20,
        sortBy: [
          {
            id: 'auditTimestamp',
            desc: true,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div>
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
        }}
      >
        <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom'}>
          <Row className={'flex-fill'}>
            <Col md={3} xl={2}>
              <NavbarBrand>{'Record Views'}</NavbarBrand>
            </Col>
          </Row>
        </Navbar>
        <div className={'table-responsive'}>
          <table className={'table table-bordered'} {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr key={`files-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      key={`files-table-thead-tr-${index}-${column.id}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render('Header')}
                      <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                    </th>
                  ))}
                </tr>
              ))}
              <tr>
                <TableGlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  colSpan={visibleColumns.length}
                  label={{ singular: 'Record View', plural: 'Record Views' }}
                />
              </tr>
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any) => {
                prepareRow(row);

                return (
                  <tr key={`files-table-tr-${row.id}`} {...row.getRowProps()}>
                    {row.cells.map((cell: any) => {
                      return (
                        <td
                          key={`files-table-td-${cell.row.id}-${cell.column.id}`}
                          {...cell.getCellProps()}
                          // onClick={() => selectRow(row.original.fileId)}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePagination
          pageCount={pageCount}
          pageOptions={pageOptions}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizes={[20, 50, 100, 500]}
        />
      </form>
    </div>
  );
};
